import type { ReactNode } from "react";
import { useListContext } from "ra-core";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

import MobileHeader from "./MobileHeader";
import { MobileContent } from "./MobileContent";

/**
 * Search that lives in the header rather than under it.
 *
 * MobileHeader is fixed, and a List renders its filter row in normal flow -- so
 * a screen that puts a title in the header and leaves the filters to the List
 * ends up with the search box underneath the header, unreachable. Contacts
 * avoided this by putting its filter inside the header; every other mobile list
 * had the bug. Putting it here means a new list cannot reintroduce it.
 */
const HeaderSearch = ({ placeholder }: { placeholder: string }) => {
  const { filterValues, setFilters } = useListContext();
  const value = (filterValues?.q as string) ?? "";

  return (
    <div className="relative flex-1">
      <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
      <Input
        value={value}
        onChange={(event) =>
          setFilters(
            { ...filterValues, q: event.target.value || undefined },
            [],
          )
        }
        placeholder={placeholder}
        className="pl-8 pr-8 h-9 bg-background"
        aria-label={placeholder}
      />
      {value ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-0 top-1/2 -translate-y-1/2 size-8"
          aria-label="Clear search"
          onClick={() => setFilters({ ...filterValues, q: undefined }, [])}
        >
          <X className="size-4" />
        </Button>
      ) : null}
    </div>
  );
};

/**
 * The frame every mobile list screen uses.
 *
 * Must be rendered inside a List, so the search can talk to the list context.
 * The body sits in MobileContent, which supplies the top offset clearing the
 * fixed header -- the other half of the same bug.
 */
export const MobileListPage = ({
  searchPlaceholder,
  actions,
  children,
}: {
  searchPlaceholder: string;
  actions?: ReactNode;
  children: ReactNode;
}) => (
  <>
    <MobileHeader>
      <HeaderSearch placeholder={searchPlaceholder} />
      {actions ? <div className="flex shrink-0 gap-1">{actions}</div> : null}
    </MobileHeader>
    <MobileContent>{children}</MobileContent>
  </>
);
